/**
 * 下行 Opus（RFC 6716 裸包）→ WebCodecs AudioDecoder → 48 kHz 立体声交错 float playback。
 * 须与后端一致：单声道、48 kHz、description 为 OpusHead。
 *
 * MQTT 载荷：设备侧为 **uint16 小端帧长 + L 字节 Opus**；一包内可多帧串联。
 * 若整包无法按该格式完整解析，则回退为「整包即一帧裸 Opus」（兼容旧流）。
 */

import { buildOpusHeadMono48k } from './opusHead';

export { buildOpusHeadMono48k } from './opusHead';

/** 单段 Opus 负载合理上限（与设备压缩帧一致；防坏包） */
const MQTT_OPUS_FRAME_MAX_PAYLOAD = 8192;

/**
 * 严格解析：必须消费完整个 buffer，否则视为非 framing 格式。
 * 上行拼帧 MQTT 体与下行相同：多段 `u16le(len)+opus` 串联。
 */
export function tryStrictU16lePrefixedOpusMqttPayload(payload: Uint8Array): Uint8Array[] | null {
  if (!payload || payload.length < 2) {
    return null;
  }
  let o = 0;
  const out: Uint8Array[] = [];
  while (o + 2 <= payload.length) {
    const n = payload[o]! | (payload[o + 1]! << 8);
    o += 2;
    if (n <= 0 || n > MQTT_OPUS_FRAME_MAX_PAYLOAD) {
      return null;
    }
    if (o + n > payload.length) {
      return null;
    }
    out.push(payload.subarray(o, o + n));
    o += n;
  }
  if (o !== payload.length) {
    return null;
  }
  return out;
}

/** @deprecated 请用 splitOpusMqttPayload（自动 u16le 多帧 / 裸包回退） */
export function splitOpusDownstreamPayload(
  payload: Uint8Array,
  _lengthPrefixBytes: 0 | 2 = 0
): Uint8Array[] {
  return splitOpusMqttPayload(payload);
}

function audioDataToStereoFloat32Interleaved(audioData: AudioData): {
  stereo: Float32Array;
  endTimestampUs: number;
} {
  const endTimestampUs = audioData.timestamp + audioData.duration;
  const frames = audioData.numberOfFrames;
  const out = new Float32Array(frames * 2);
  const fmt = audioData.format;

  if (fmt === 'f32-planar') {
    const plane = new Float32Array(frames);
    audioData.copyTo(plane, { planeIndex: 0 });
    for (let i = 0; i < frames; i++) {
      const s = plane[i];
      out[i * 2] = s;
      out[i * 2 + 1] = s;
    }
  } else if (fmt === 's16-planar') {
    const plane = new Int16Array(frames);
    audioData.copyTo(plane, { planeIndex: 0 });
    const sc = 1 / 32768;
    for (let i = 0; i < frames; i++) {
      const s = plane[i] * sc;
      out[i * 2] = s;
      out[i * 2 + 1] = s;
    }
  } else {
    const plane = new Float32Array(frames);
    try {
      audioData.copyTo(plane, { planeIndex: 0 });
      for (let i = 0; i < frames; i++) {
        const s = plane[i];
        out[i * 2] = s;
        out[i * 2 + 1] = s;
      }
    } catch {
      audioData.close();
      throw new Error(`Opus output format unsupported: ${String(fmt)}`);
    }
  }
  audioData.close();
  return { stereo: out, endTimestampUs };
}

type Waiter = { resolve: (v: Float32Array) => void; reject: (e: Error) => void };

/**
 * 按设备维持一条解码时间线；切换设备时请 close() 并新建实例。
 */
export class OpusWebCodecsDownstreamSession {
  private decoder: AudioDecoder | null = null;
  private configured = false;
  private nextTimestampUs = 0;
  private readonly waiting: Waiter[] = [];

  constructor(private readonly deviceId: string) {}

  getDeviceId(): string {
    return this.deviceId;
  }

  static isSupported(): boolean {
    return typeof AudioDecoder !== 'undefined' && typeof EncodedAudioChunk !== 'undefined';
  }

  async ensureConfigured(): Promise<boolean> {
    if (this.decoder && this.configured) {
      return true;
    }
    if (!OpusWebCodecsDownstreamSession.isSupported()) {
      console.warn('[WaveLink Opus] 当前环境不支持 WebCodecs AudioDecoder');
      return false;
    }
    const head = buildOpusHeadMono48k();
    const description = head.slice().buffer as ArrayBuffer;
    const config: AudioDecoderConfig = {
      codec: 'opus',
      sampleRate: 48000,
      numberOfChannels: 1,
      description,
    };
    let supported = false;
    try {
      const check = await AudioDecoder.isConfigSupported(config);
      supported = !!check.supported;
    } catch {
      supported = false;
    }
    if (!supported) {
      console.warn('[WaveLink Opus] AudioDecoder.isConfigSupported(opus) 未通过', { deviceId: this.deviceId });
      return false;
    }

    this.decoder = new AudioDecoder({
      output: (audioData) => {
        try {
          const { stereo, endTimestampUs } = audioDataToStereoFloat32Interleaved(audioData);
          this.nextTimestampUs = endTimestampUs;
          const w = this.waiting.shift();
          if (w) {
            w.resolve(stereo);
          }
        } catch (e) {
          const w = this.waiting.shift();
          if (w) {
            w.reject(e instanceof Error ? e : new Error(String(e)));
          }
        }
      },
      error: (e) => {
        const err = e instanceof Error ? e : new Error(String(e));
        while (this.waiting.length) {
          const w = this.waiting.shift();
          w?.reject(err);
        }
      },
    });

    try {
      await this.decoder.configure(config);
      this.configured = true;
      this.nextTimestampUs = 0;
      return true;
    } catch (e) {
      console.error('[WaveLink Opus] AudioDecoder.configure 失败', e);
      try {
        this.decoder.close();
      } catch {
        /* ignore */
      }
      this.decoder = null;
      this.configured = false;
      return false;
    }
  }

  decodePacket(packet: Uint8Array): Promise<Float32Array> {
    return new Promise((resolve, reject) => {
      if (!this.decoder || !this.configured) {
        reject(new Error('Opus decoder not ready'));
        return;
      }
      if (!(packet.byteLength > 0)) {
        reject(new Error('empty opus packet'));
        return;
      }
      this.waiting.push({ resolve, reject });
      try {
        const chunk = new EncodedAudioChunk({
          type: 'key',
          timestamp: this.nextTimestampUs,
          data: packet,
        });
        this.decoder.decode(chunk);
      } catch (err) {
        this.waiting.pop();
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  close(): void {
    while (this.waiting.length) {
      const w = this.waiting.shift();
      w?.reject(new Error('Opus session closed'));
    }
    if (this.decoder && this.decoder.state !== 'closed') {
      try {
        this.decoder.close();
      } catch {
        /* ignore */
      }
    }
    this.decoder = null;
    this.configured = false;
    this.nextTimestampUs = 0;
  }
}

/** 将 MQTT 下行二进制拆成多段裸 Opus 包（u16le 长度前缀多帧，或整包一帧） */
export function splitOpusMqttPayload(payload: Uint8Array): Uint8Array[] {
  if (!payload || payload.length === 0) {
    return [];
  }
  const framed = tryStrictU16lePrefixedOpusMqttPayload(payload);
  if (framed && framed.length > 0) {
    return framed;
  }
  return [payload];
}
